import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../api";
import type { Turbine } from "../../types";
import { tg } from "../../telegram";
import { useT } from "../../i18n";

/**
 * Dedicated tab for emergency submissions. Holds a single big red button
 * with a clear description above it — so operators don't tap "Emergency"
 * by mistake when they meant the plan report.
 */
export default function EmergencyLanding() {
  const { t } = useT();
  const navigate = useNavigate();
  const [turbines, setTurbines] = useState<Turbine[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Turbine[]>("/api/turbines")
      .then(list => {
        setTurbines(list);
        if (list.length > 0) setSelectedId(list[0].id);
      })
      .catch((e: ApiError) => setError(e.message));
  }, []);

  const go = () => {
    if (!selectedId) return;
    tg()?.HapticFeedback?.impactOccurred("medium");
    navigate(`/report/emergency?turbine_id=${selectedId}`);
  };

  return (
    <div className="emergency-landing">
      <h1>{t("emergency.title")}</h1>

      <div className="emergency-explain">
        <div className="emergency-explain-icon">⚠️</div>
        <div className="emergency-explain-text">
          <b>{t("emergency.landing_what")}</b>
          <p>{t("emergency.landing_when")}</p>
          <p style={{ color: "var(--destructive)" }}>
            <b>{t("emergency.landing_warn")}</b>
          </p>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}

      {!turbines && !error && (
        <div className="empty-state" style={{ padding: 40 }}>
          <span className="spinner" />
        </div>
      )}

      {turbines && turbines.length > 1 && (
        <div className="turbine-switcher" style={{ justifyContent: "center", marginTop: 16 }}>
          {turbines.map(tb => (
            <button
              key={tb.id}
              className={"turbine-chip" + (tb.id === selectedId ? " active" : "")}
              onClick={() => setSelectedId(tb.id)}
            >
              <span className="turbine-chip-name">{tb.name}</span>
            </button>
          ))}
        </div>
      )}

      {turbines && turbines.length > 0 && (
        <div className="emergency-center">
          <button
            className="emergency-big-btn"
            onClick={go}
            disabled={!selectedId}
          >
            <span className="emergency-big-icon">🚨</span>
            <span className="emergency-big-label">{t("emergency.landing_btn")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
